import * as Sentry from "@sentry/react";
import { apiFetch, expectJsonBody } from "../api";
import type {
    StaffNameFormat,
    StaffNameFormatPreview,
} from "@/common/components/staff-name-format/staffNameFormatOptions";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Update the calendar display mode for an account.
 * @param accountId - The account ID to update
 * @param calendarMode - The new calendar mode ('doctors' | 'resources')
 */
export async function updateCalendarMode(accountId: string, calendarMode: "doctors" | "resources"): Promise<void> {
    if (!UUID_RE.test(accountId)) throw new Error("Invalid accountId");
    try {
        await apiFetch(`account/${accountId}/calendar-mode`, {
            method: "PATCH",
            json: { calendar_mode: calendarMode },
        });
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
}

/**
 * Set the clinic's time zone.
 *
 * Cornerstone stores appointment date/time with no zone attached, and the bridge interprets those
 * naive values in `account.clinic_time_zone`. A null or wrong value is an off-by-the-offset error
 * on every appointment forever, so this write is load-bearing rather than cosmetic.
 *
 * The server re-normalizes and rejects anything it can't map, because the column is a Postgres
 * enum: an unrecognized id would otherwise be a 500 from the database rather than a 400 from us.
 *
 * @param accountId - The account (clinic) ID to update
 * @param clinicTimeZone - An IANA zone id, e.g. "America/Chicago"
 */
export async function updateClinicTimeZone(accountId: string, clinicTimeZone: string): Promise<void> {
    if (!UUID_RE.test(accountId)) throw new Error("Invalid accountId");
    try {
        await apiFetch(`account/${accountId}/clinic-timezone`, {
            method: "PATCH",
            json: { clinic_time_zone: clinicTimeZone },
        });
    } catch (error: any) {
        Sentry.captureException(error);
        throw error;
    }
}

interface StaffNameFormatResponse<T> {
    status: string;
    data: T;
}

/** EN-1242: every bridge-synced staff row rendered in all five name formats. */
export async function fetchStaffNameFormatPreview(accountId: string): Promise<StaffNameFormatPreview> {
    if (!UUID_RE.test(accountId)) throw new Error("Invalid accountId");
    const endpoint = `account/${accountId}/staff-name-format/preview`;
    const body = expectJsonBody(
        await apiFetch<StaffNameFormatResponse<StaffNameFormatPreview>>(endpoint, { method: "GET" }),
        endpoint,
    );
    return body.data;
}

/**
 * `reapplyPending`: the setting saved but the server's immediate rename failed; a staff sync
 * (which re-ingests every non-manual name in the new format) finishes it. Absent means false.
 */
export type StaffNameFormatSaveResult = {
    staff_name_format: StaffNameFormat;
    updated: number;
    missingParts: number;
    reapplyPending?: boolean;
};

/**
 * The HTTP status carried by an error thrown from `apiFetch`, or undefined when there is none
 * (network failure, abort, non-HTTP throw). ky's HTTPError only exposes `response.status`; some
 * callers copy it onto `status`, which wins when present.
 */
export function getHttpStatus(error: unknown): number | undefined {
    if (!error || typeof error !== "object") return undefined;
    const own = (error as { status?: unknown }).status;
    if (typeof own === "number") return own;
    const fromResponse = (error as { response?: { status?: unknown } }).response?.status;
    return typeof fromResponse === "number" ? fromResponse : undefined;
}

/** PATCH staff-name-format outcomes that are expected, not faults: 403 non-owner, 409 sync running. */
const EXPECTED_STAFF_NAME_FORMAT_STATUSES = [403, 409];

/**
 * EN-1242: save the clinic's staff name format. The server re-applies it to every non-manual
 * staff row before responding; `missingParts` counts rows it couldn't reformat yet because they
 * haven't been synced since this feature shipped, and the caller decides whether to run a sync.
 *
 * Thrown errors always carry a numeric `status` when the server responded. `apiFetch` rethrows
 * ky's HTTPError, which only exposes `error.response.status`, so it is copied onto the error here.
 * A 409 means a staff sync is mid-flight; the caller shows a retry. A 403 means the viewer isn't
 * the practice owner; the caller explains that instead of retrying.
 */
export async function updateStaffNameFormat(
    accountId: string,
    format: StaffNameFormat,
): Promise<StaffNameFormatSaveResult> {
    if (!UUID_RE.test(accountId)) throw new Error("Invalid accountId");
    const endpoint = `account/${accountId}/staff-name-format`;
    try {
        const body = expectJsonBody(
            await apiFetch<StaffNameFormatResponse<StaffNameFormatSaveResult>>(endpoint, {
                method: "PATCH",
                json: { staff_name_format: format },
                skipSentryOnStatuses: EXPECTED_STAFF_NAME_FORMAT_STATUSES,
            }),
            endpoint,
        );
        return body.data;
    } catch (error) {
        const status = getHttpStatus(error);
        if (status !== undefined) (error as { status?: number }).status = status;
        // Expected outcomes, not faults (see above); apiFetch is told to skip them too.
        if (status === undefined || !EXPECTED_STAFF_NAME_FORMAT_STATUSES.includes(status))
            Sentry.captureException(error);
        throw error;
    }
}
